/*
 * Lot 10 : fonctions pures de l'onglet « Analyses ». Chacune porte une carte (components/analyses, pages/Analyses.js).
 * Source unique : `index` (selectors/index.js). Les cartes « réelles » ne lisent que les événements `source !== 'simulation'`
 * (elles sont donc identiques bouton Collecte à 0 ou à 1) ; les cartes « simulées » lisent les COLLECTE simulées, présentes
 * dans l'index seulement bouton Collecte à 1 (sans elles, chaque fonction rend un résultat vide et une phrase honnête).
 * Les phrases de constat (`constat*`) sont calculées à partir des résultats, jamais écrites en dur.
 */

const MS_JOUR = 86400000;
const REEL = (e) => e.source !== 'simulation';
const PASSAGE = (e) => e.type === 'LAVAGE' || e.type === 'IMPOSSIBLE';
const taux = (e) => (e.type === 'COLLECTE' && e.detail && typeof e.detail.tauxRemplissage === 'number' ? e.detail.tauxRemplissage : null);
const moyenne = (v) => (v.length > 0 ? v.reduce((a, b) => a + b, 0) / v.length : null);
const versMs = (iso) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d); };

/** Mois pleins écoulés entre deux dates Y-m-d (a <= b). */
export function moisEntre(a, b) {
    const [y1, m1, d1] = a.split('-').map(Number);
    const [y2, m2, d2] = b.split('-').map(Number);
    return (y2 - y1) * 12 + (m2 - m1) - (d2 < d1 ? 1 : 0);
}

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
export const nomMois = (ym) => MOIS[Number(ym.slice(5, 7)) - 1];

/** Nombre français à `d` décimales, virgule, sans « ,0 » final (« 50,5 », « 3 »). */
export function nbFr(x, d = 1) {
    const s = x.toFixed(d);
    return (d > 0 ? s.replace(/\.?0+$/, '') : s).replace('.', ',');
}
export const pctFr = (ratio, d = 1) => `${nbFr(ratio * 100, d)} %`;

/** Premier passage réel d'un PAV (tout événement réel hors PLANIFIÉ) : date Y-m-d, ou null. */
function premierPassageReel(evts) {
    for (const e of evts) if (REEL(e) && e.type !== 'PLANIFIÉ' && e.type !== 'COLLECTE' && e.type !== 'COLLECTE_IMPOSSIBLE') return e.date;
    return null;
}

function motifPrincipal(motifs) {
    let best = null;
    for (const [m, nb] of motifs) if (best === null || nb > best.nb || (nb === best.nb && m.localeCompare(best.m) < 0)) best = {m, nb};
    return best ? best.m : null;
}

// ---------------------------------------------------------------------------------------------------------------------
// 1. Récidive des lavages impossibles (réel)
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Sur les passages réels (LAVAGE / IMPOSSIBLE, par date puis heure) de chaque PAV : probabilité que le passage suivant
 * soit impossible selon que le précédent l'était ou non. `nApres` = nombre de passages qui suivent un impossible.
 * `partTop` : les `nTop` (100) PAV les plus touchés portent `part` des impossibles. `top` : les 10 PAV les plus touchés.
 */
export function recidiveImpossibles(index, {nTop = 100, tailleTop = 10} = {}) {
    let nImp = 0; let impApresImp = 0; let nLave = 0; let impApresLave = 0;
    const parPav = new Map();
    for (const [pavId, evts] of index.evenementsParPav) {
        const passages = evts.filter((e) => REEL(e) && PASSAGE(e));
        passages.forEach((e, i) => {
            if (e.type === 'IMPOSSIBLE') {
                let c = parPav.get(pavId);
                if (!c) parPav.set(pavId, (c = {nb: 0, motifs: new Map()}));
                c.nb++;
                for (const m of e.motifs || []) c.motifs.set(m, (c.motifs.get(m) || 0) + 1);
            }
            if (i === 0) return;
            const impossible = e.type === 'IMPOSSIBLE';
            if (passages[i - 1].type === 'IMPOSSIBLE') { nImp++; if (impossible) impApresImp++; } else { nLave++; if (impossible) impApresLave++; }
        });
    }
    const classes = [...parPav.entries()].map(([pavId, c]) => {
        const pav = index.pavById.get(pavId);
        return {pavId, nomenclature: pav ? pav.nomenclature : pavId, nb: c.nb, motif: motifPrincipal(c.motifs)};
    }).sort((a, b) => b.nb - a.nb || a.nomenclature.localeCompare(b.nomenclature, 'fr', {numeric: true}));
    const total = classes.reduce((s, c) => s + c.nb, 0);
    const nbTop = Math.min(nTop, classes.length);
    return {
        pApresImpossible: nImp > 0 ? impApresImp / nImp : null,
        pApresLave: nLave > 0 ? impApresLave / nLave : null,
        nApres: nImp,
        nApresLave: nLave,
        nImpossibles: total,
        nbPavTouches: classes.length,
        partTop: {n: nbTop, part: total > 0 ? classes.slice(0, nbTop).reduce((s, c) => s + c.nb, 0) / total : null},
        top: classes.slice(0, tailleTop),
    };
}

export function constatRecidive(r) {
    if (r.nImpossibles === 0) return 'Aucun passage impossible enregistré : aucune récidive à mesurer.';
    const part = `${r.partTop.n} PAV ${r.partTop.n === 1 ? 'porte' : 'portent'} ${pctFr(r.partTop.part, 0)} des impossibles.`;
    if (r.pApresImpossible === null) return `Aucun passage impossible n'est suivi d'un autre passage : la récidive n'est pas mesurable. ${part}`;
    const lave = r.pApresLave === null ? 'aucun passage mesurable' : pctFr(r.pApresLave);
    return `Après un passage impossible, le suivant l'est à ${pctFr(r.pApresImpossible)} ; après un lavage réussi, à ${lave}. ${part}`;
}

// ---------------------------------------------------------------------------------------------------------------------
// 2. Défauts de maintenance selon l'âge (réel)
// ---------------------------------------------------------------------------------------------------------------------

const ligneTaux = (o) => ({...o, taux: o.actes > 0 ? o.defauts / o.actes : null});

/**
 * Actes de maintenance préventive réels (MAINT_PREV) : taux de défaut (verdict `probleme`) par tranche de 6 mois d'âge du
 * PAV à la date de l'acte (âge = mois pleins depuis son premier passage réel) et par nombre de bacs (`quantite`).
 */
export function defautsParAge(index) {
    const tranches = new Map();
    const bacs = new Map();
    const cumul = (m, cle) => { let c = m.get(cle); if (!c) m.set(cle, (c = {actes: 0, defauts: 0})); return c; };
    for (const [pavId, evts] of index.evenementsParPav) {
        const pav = index.pavById.get(pavId);
        const premier = premierPassageReel(evts);
        for (const e of evts) {
            if (e.type !== 'MAINT_PREV' || !REEL(e)) continue;
            const defaut = e.verdict === 'probleme' ? 1 : 0;
            const k = Math.floor(moisEntre(premier, e.date) / 6);
            const t = cumul(tranches, k); t.actes++; t.defauts += defaut;
            if (pav && Number.isFinite(pav.quantite)) { const b = cumul(bacs, pav.quantite); b.actes++; b.defauts += defaut; }
        }
    }
    const lignesTranches = [...tranches.entries()].sort((a, b) => a[0] - b[0])
        .map(([k, c]) => ligneTaux({tranche: `${6 * k}-${6 * k + 5} mois`, de: 6 * k, a: 6 * k + 5, ...c}));
    const parBacs = [...bacs.entries()].sort((a, b) => a[0] - b[0]).map(([n, c]) => ligneTaux({bacs: n, ...c}));
    const somme = (lignes) => lignes.reduce((s, l) => ({actes: s.actes + l.actes, defauts: s.defauts + l.defauts}), {actes: 0, defauts: 0});
    return {
        tranches: lignesTranches,
        parBacs,
        avant6: ligneTaux(somme(lignesTranches.filter((l) => l.de < 6))),
        apres6: ligneTaux(somme(lignesTranches.filter((l) => l.de >= 6))),
    };
}

export function constatAge(r) {
    if (r.avant6.actes + r.apres6.actes === 0) return 'Aucun acte de maintenance préventive enregistré : pas de taux de défaut à mesurer.';
    const t = (x) => (x.taux === null ? 'taux non mesurable' : pctFr(x.taux));
    const age = `${t(r.avant6)} de défaut avant 6 mois, ${t(r.apres6)} après`;
    const un = r.parBacs.find((b) => b.bacs === 1);
    const deux = r.parBacs.find((b) => b.bacs === 2);
    if (!un || !deux) return `${age}.`;
    return `${age} ; ${t(un)} à un bac, ${t(deux)} à deux.`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Cartes simulées (S10a-c) : `index` doit avoir été construit bouton Collecte à 1, sinon aucune COLLECTE n'est visible.
// ---------------------------------------------------------------------------------------------------------------------

const SEUIL_ECART = 0.03; // en dessous de 3 % d'écart relatif, on ne parle pas d'effet
const SEUIL_PENTE = 0.005; // en dessous de 0,5 point de remplissage perdu par mois, on ne parle pas de dérive
const titre = (s) => (s || '').toLowerCase().replace(/(^|[\s'-])(\p{L})/gu, (m, sep, c) => `${sep}${c.toUpperCase()}`);
const ET = (liste) => (liste.length <= 1 ? liste.join('') : `${liste.slice(0, -1).join(', ')} et ${liste[liste.length - 1]}`);

// ---------------------------------------------------------------------------------------------------------------------
// 3. Effet du lavage sur l'usage (simulé, S10a)
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Estimateur apparié par borne. Pour chaque PAV ayant deux IMPOSSIBLE réels consécutifs (on retient le premier 2e impossible) :
 * `avant` = moyenne des `fenetre` (3) collectes qui le précèdent, `pendant` = moyenne des 3 collectes qui le suivent (jusqu'au
 * premier LAVAGE réel postérieur), `apres` = moyenne des 3 collectes qui suivent ce LAVAGE (absent si pas de lavage ou de collecte).
 * `ecart` = moyenne des (pendant - avant) / avant par PAV ; `rebond` = moyenne des (apres - pendant) / pendant. `courbe` : par
 * semaine relative (S-4 .. S+6 ; S0 = première collecte après le 2e impossible), moyenne des écarts relatifs à `avant` (fraction).
 */
export function effetLavage(index, {fenetre = 3} = {}) {
    const ecarts = []; const rebonds = []; const semaines = new Map();
    const avants = []; const pendants = []; const apres = [];
    for (const evts of index.evenementsParPav.values()) {
        let consecutifs = 0; let iQual = -1;
        for (let i = 0; i < evts.length; i++) {
            const e = evts[i];
            if (!REEL(e)) continue;
            if (e.type === 'IMPOSSIBLE') { consecutifs++; if (consecutifs >= 2) { iQual = i; break; } } else if (e.type === 'LAVAGE') consecutifs = 0;
        }
        if (iQual < 0) continue;
        const coll = (de, a) => { const r = []; for (let i = de; i < a; i++) { const t = taux(evts[i]); if (t !== null) r.push({i, t, date: evts[i].date}); } return r; };
        let iLavage = evts.length;
        for (let i = iQual + 1; i < evts.length; i++) if (REEL(evts[i]) && evts[i].type === 'LAVAGE') { iLavage = i; break; }
        const avantC = coll(0, iQual).slice(-fenetre);
        const pendantC = coll(iQual + 1, iLavage).slice(0, fenetre);
        const apresC = iLavage < evts.length ? coll(iLavage + 1, evts.length).slice(0, fenetre) : [];
        const avant = moyenne(avantC.map((c) => c.t));
        const pendant = moyenne(pendantC.map((c) => c.t));
        if (!avant || pendant === null) continue;
        ecarts.push((pendant - avant) / avant);
        avants.push(avant); pendants.push(pendant);
        if (apresC.length > 0 && pendant > 0) { const ap = moyenne(apresC.map((c) => c.t)); rebonds.push((ap - pendant) / pendant); apres.push(ap); }
        const qDate = evts[iQual].date;
        for (const c of coll(0, evts.length)) {
            const jours = Math.round((versMs(c.date) - versMs(qDate)) / MS_JOUR);
            const w = Math.floor((jours === 0 && c.i < iQual ? -1 : jours) / 7);
            if (w < -4 || w > 6) continue;
            if (!semaines.has(w)) semaines.set(w, []);
            semaines.get(w).push((c.t - avant) / avant);
        }
    }
    const courbe = [];
    for (let w = -4; w <= 6; w++) courbe.push({x: `S${w > 0 ? '+' : ''}${w}`, y: semaines.has(w) ? moyenne(semaines.get(w)) : null});
    return {
        ecart: moyenne(ecarts), rebond: moyenne(rebonds), n: ecarts.length, nRebond: rebonds.length, nbPavConcernes: ecarts.length,
        avant: moyenne(avants), pendant: moyenne(pendants), apres: moyenne(apres), courbe,
    };
}

export function constatEffetLavage(r) {
    if (r.ecart === null) return 'Aucun écart mesurable : pas assez de collectes autour de deux lavages impossibles pour comparer.';
    const bornes = `${r.n} borne${r.n > 1 ? 's' : ''}`;
    if (Math.abs(r.ecart) < SEUIL_ECART) return `Aucun écart mesurable : sur ${bornes}, le remplissage ne change pas après deux lavages impossibles.`;
    const sens = r.ecart < 0 ? 'baisse' : 'augmente';
    const rebond = r.rebond === null ? '' : ` puis ${r.rebond >= 0 ? 'remonte' : 'baisse encore'} de ${pctFr(Math.abs(r.rebond), 0)} dès le lavage suivant`;
    return `Sur ${bornes}, après deux lavages impossibles le remplissage ${sens} de ${pctFr(Math.abs(r.ecart), 0)}${rebond}.`;
}

// ---------------------------------------------------------------------------------------------------------------------
// 4. Quartiers en dérive (simulé, S10b)
// ---------------------------------------------------------------------------------------------------------------------

const ym = (iso) => iso.slice(0, 7);
function moisPrecedent(m) { const [y, mo] = m.split('-').map(Number); return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, '0')}`; }

/** Pente (par mois) de la droite des moindres carrés sur des points {x, y}. */
function pente(points) {
    const n = points.length;
    const mx = points.reduce((s, p) => s + p.x, 0) / n;
    const my = points.reduce((s, p) => s + p.y, 0) / n;
    const den = points.reduce((s, p) => s + (p.x - mx) ** 2, 0);
    return den === 0 ? 0 : points.reduce((s, p) => s + (p.x - mx) * (p.y - my), 0) / den;
}

/**
 * Pente mensuelle du remplissage moyen par patrimoine (circuit) sur les `nMois` (5) derniers mois de collecte : régression
 * linéaire simple sur les moyennes mensuelles (mois à moins de `minParMois` collectes ignorés ; 3 mois mesurés au moins).
 * `circuits` triés du plus en baisse au plus en hausse ; `pente` = variation de remplissage par mois (fraction : -0,01 = -1 point).
 */
export function derives(index, {nMois = 5, minParMois = 5} = {}) {
    let dernier = null;
    for (const evts of index.evenementsParPav.values()) {
        for (let i = evts.length - 1; i >= 0; i--) {
            if (taux(evts[i]) !== null) { if (dernier === null || evts[i].date > dernier) dernier = evts[i].date; break; }
        }
    }
    if (dernier === null) return {debut: null, fin: null, circuits: []};
    const mois = [ym(dernier)];
    while (mois.length < nMois) mois.unshift(moisPrecedent(mois[0]));
    const fenetre = new Set(mois);
    const parCircuit = new Map();
    for (const [pavId, evts] of index.evenementsParPav) {
        const pav = index.pavById.get(pavId);
        for (const e of evts) {
            const t = taux(e);
            if (t === null || !fenetre.has(ym(e.date))) continue;
            const id = e.patrimoineId ?? (pav && pav.patrimoineId);
            if (id == null) continue;
            if (!parCircuit.has(id)) parCircuit.set(id, new Map());
            const m = parCircuit.get(id);
            if (!m.has(ym(e.date))) m.set(ym(e.date), []);
            m.get(ym(e.date)).push(t);
        }
    }
    const circuits = [];
    for (const [id, m] of parCircuit) {
        const serie = mois.map((x) => ({x, y: m.has(x) && m.get(x).length >= minParMois ? moyenne(m.get(x)) : null}));
        const pts = serie.map((p, i) => ({x: i, y: p.y})).filter((p) => p.y !== null);
        if (pts.length < 3) continue;
        const patrimoine = index.patrimoineById.get(id);
        circuits.push({patrimoineId: id, libelle: patrimoine ? patrimoine.libelle : String(id), pente: pente(pts), serie, n: [...m.values()].reduce((s, v) => s + v.length, 0)});
    }
    circuits.sort((a, b) => a.pente - b.pente || a.libelle.localeCompare(b.libelle, 'fr', {numeric: true}));
    return {debut: mois[0], fin: mois[mois.length - 1], circuits};
}

export function constatDerives(r) {
    const tete = r.circuits[0];
    if (!tete || tete.pente > -SEUIL_PENTE) return `Aucune dérive mesurable : aucun circuit ne perd plus de ${nbFr(SEUIL_PENTE * 100)} point de remplissage par mois.`;
    const pts = -tete.pente * 100;
    return `Le circuit ${tete.libelle} perd ${nbFr(pts)} point${pts >= 2 ? 's' : ''} de remplissage par mois depuis ${nomMois(r.debut)}.`;
}

// ---------------------------------------------------------------------------------------------------------------------
// 5. Déclassés : où et sur quelles bornes (simulé, S10c)
// ---------------------------------------------------------------------------------------------------------------------

const tauxDe = (c) => (c.collectes > 0 ? c.declasses / c.collectes : null);
const somme = (liste) => liste.reduce((s, c) => ({collectes: s.collectes + c.collectes, declasses: s.declasses + c.declasses}), {collectes: 0, declasses: 0});

/**
 * Taux de déclassement (COLLECTE_IMPOSSIBLE / toutes les collectes) par commune (clé normalisée ; top 5 ; communes à moins
 * de `minCollectes` collectes écartées) et par âge de la borne à la date de la collecte (mois pleins depuis son premier
 * passage réel : < 6 mois / >= 6 mois ; collectes antérieures au premier passage réel et PAV sans passage : hors tranche).
 * `communesCles` : communes du top dont le taux vaut au moins `seuil` (1,5) fois celui des autres communes ;
 * `facteurCommunes` / `facteurAge` : rapport des taux (communes signalées / autres ; < 6 mois / >= 6 mois), null si non mesurable ou sous le seuil.
 */
export function declassesProfil(index, {minCollectes = 30, seuil = 1.5, nTop = 5} = {}) {
    const communes = new Map();
    const age = {jeunes: {collectes: 0, declasses: 0}, anciens: {collectes: 0, declasses: 0}};
    const global = {collectes: 0, declasses: 0};
    for (const [pavId, evts] of index.evenementsParPav) {
        const pav = index.pavById.get(pavId);
        const premier = premierPassageReel(evts);
        for (const e of evts) {
            const declasse = e.type === 'COLLECTE_IMPOSSIBLE';
            if (!declasse && e.type !== 'COLLECTE') continue;
            global.collectes++; if (declasse) global.declasses++;
            if (pav && pav.communeCle) {
                let c = communes.get(pav.communeCle);
                if (!c) communes.set(pav.communeCle, (c = {cle: pav.communeCle, libelle: titre(pav.communeLibelle), collectes: 0, declasses: 0}));
                c.collectes++; if (declasse) c.declasses++;
            }
            if (premier !== null && e.date >= premier) {
                const a = moisEntre(premier, e.date) < 6 ? age.jeunes : age.anciens;
                a.collectes++; if (declasse) a.declasses++;
            }
        }
    }
    const toutes = [...communes.values()];
    const hors = (liste) => { const cles = new Set(liste.map((c) => c.cle)); return somme(toutes.filter((c) => !cles.has(c.cle))); };
    const rapport = (a, b) => (tauxDe(a) !== null && tauxDe(b) ? tauxDe(a) / tauxDe(b) : null);
    const lignes = toutes.filter((c) => c.collectes >= minCollectes)
        .map((c) => ({...c, taux: tauxDe(c)}))
        .sort((a, b) => b.taux - a.taux || b.collectes - a.collectes || a.libelle.localeCompare(b.libelle, 'fr'))
        .slice(0, nTop);
    const signalees = lignes.filter((c) => c.declasses > 0 && rapport(c, hors([c])) >= seuil);
    const fCommunes = signalees.length > 0 ? rapport(somme(signalees), hors(signalees)) : null;
    const fAge = rapport(age.jeunes, age.anciens);
    return {
        global: {...global, taux: tauxDe(global)},
        communes: lignes,
        ages: [{tranche: 'Moins de 6 mois', ...age.jeunes, taux: tauxDe(age.jeunes)}, {tranche: '6 mois et plus', ...age.anciens, taux: tauxDe(age.anciens)}],
        communesCles: signalees.map((c) => c.cle),
        communesLibelles: signalees.map((c) => c.libelle),
        facteurCommunes: fCommunes !== null && fCommunes >= seuil ? fCommunes : null,
        facteurAge: fAge !== null && fAge >= seuil ? fAge : null,
    };
}

export function constatDeclasses(r) {
    if (r.global.declasses === 0) return 'Aucun déclassé enregistré : rien à localiser.';
    const parts = [];
    if (r.facteurCommunes !== null) parts.push(`${nbFr(r.facteurCommunes, 1)}× plus fréquents à ${ET(r.communesLibelles)}`);
    if (r.facteurAge !== null) parts.push(`${nbFr(r.facteurAge, 1)}× plus fréquents sur les bornes de moins de 6 mois`);
    if (parts.length === 0) return 'Aucun écart mesurable : les déclassés se répartissent de façon homogène entre les communes et les âges de bornes.';
    return `Les déclassés sont ${parts.join(', et ')}.`;
}

/**
 * Mention « signal injecté » sous une carte simulée, lue dans `snapshot.rse.collecte.signaux` ; null si le signal est absent
 * (snapshot sans signaux injectés). `cle` : 'effetLavage' | 'derive' | 'declasses' | 'condamnee' | 'defautBloquant'.
 */
export function mentionSignal(snapshot, cle, index = null) {
    const signaux = snapshot && snapshot.rse && snapshot.rse.collecte && snapshot.rse.collecte.signaux;
    const s = signaux && signaux[cle];
    if (!s) return null;
    if (cle === 'effetLavage') return `Signal injecté dans la simulation : effet du lavage sur ${s.nbPavConcernes} PAV.`;
    if (cle === 'derive') {
        const p = index && index.patrimoineById.get(s.patrimoineId);
        return `Signal injecté dans la simulation : ${p ? p.libelle : `circuit ${s.patrimoineId}`} perd ${nbFr(s.pointsParMois)} point par mois depuis le ${s.depuis.split('-').reverse().join('/')}.`;
    }
    if (cle === 'condamnee') return `Signal injecté dans la simulation : collecte ×${nbFr(s.facteur)} tant qu'une borne est condamnée (${s.nbPavConcernes} PAV concernés).`;
    if (cle === 'defautBloquant') return `Signal injecté dans la simulation : collecte ×${nbFr(s.facteur)} après un défaut de ${ET((s.elements || []).map((e) => e.toLowerCase()))}, ${s.maxSemaines} semaines au plus (${s.nbPavConcernes} PAV concernés).`;
    if (cle === 'declasses') return `Signal injecté dans la simulation : déclassés ×${s.facteur} à ${ET((s.communes || []).map(titre))} et sur les bornes de moins de 6 mois.`;
    return null;
}
